import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { OpenStreetMapProvider } from 'leaflet-geosearch';

const markerIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});

interface Props {
  location: string;
  name: string;
  setFieldValue: (field: string, value: any) => void;
  latitude: number;
  longitude: number;
}
type SearchResult = {
  x: number; 
  y: number; 
  label: string;
};

const MapUpdater: React.FC<{ position: [number, number] }> = ({ position }) => {
  const map = useMap();

  useEffect(() => {
    map.setView(position);
  }, [position]);

  return null;
};

const LocationSearch: React.FC<Props> = ({
  location,
  setFieldValue,
  name,
  latitude,
  longitude
}) => {
  const [position, setPosition] = useState<[number, number]>(() => {
    return latitude !== undefined && longitude !== undefined
      ? [latitude, longitude]
      : [51.505, -0.09];
  });
  const [suggestions, setSuggestions] = useState<SearchResult[]>([]);
  const [inputValue, setInputValue] = useState(location);
  const isPropertySelected = latitude !== 0 || longitude !== 0;
  const provider = new OpenStreetMapProvider();

  useEffect(() => {
    if (latitude && longitude) {
      setPosition([latitude, longitude]);
    }
  }, [latitude, longitude]);

  const handleSearch = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setInputValue(query);
    setFieldValue('location', query);

    if (query.length > 2) {
      const results = await provider.search({ query });
      setSuggestions(results);
    } else {
      setSuggestions([]);
    }
  };

  const handleSelectSuggestion = (suggestion: SearchResult) => {
    const newLat = suggestion.y;
    const newLng = suggestion.x;

    setFieldValue('latitude', newLat);
    setFieldValue('longitude', newLng);
    setFieldValue('location', suggestion.label);
    setPosition([newLat, newLng]);
    setInputValue(suggestion.label);
    setSuggestions([]);
  };

  useEffect(() => {
    setInputValue(location);
  }, [location]);

  return (
    <div style={{ position: 'relative' }}>
      <input
        type="text"
        name="location"
        className="form-control mb-2"
        placeholder="Search location"
        value={inputValue}
        onChange={handleSearch}
      />

      {suggestions.length > 0 && (
        <ul
          className="list-group position-absolute w-100"
          style={{
            zIndex: 1000,
            maxHeight: 200,
            overflowY: 'auto',
            backgroundColor: '#fff', 
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)' 
          }}
        >
          {suggestions.map((result, idx) => (
            <li
              key={idx}
              className="list-group-item list-group-item-action"
              onClick={() => handleSelectSuggestion(result)}
              style={{ cursor: 'pointer' }}
            >
              {result.label}
            </li>
          ))}
        </ul>
      )}
      {isPropertySelected && (
        <MapContainer
          center={position}
          zoom={13}
          minZoom={0}
          maxZoom={24}
          style={{ height: '250px', width: '100%', marginTop: '1rem' }}
        >
          <TileLayer
            attribution="&copy; OpenStreetMap"
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker
            position={position}
            icon={markerIcon}
            draggable={true}
            eventHandlers={{
              dragend: async e => {
                const marker = e.target;
                const newPos = marker.getLatLng();
                const newLat = newPos.lat;
                const newLng = newPos.lng;

                // Update marker position
                setPosition([newLat, newLng]);
                setFieldValue('latitude', newLat);
                setFieldValue('longitude', newLng);

                // Reverse geocode to get address
                try {
                  const response = await fetch(
                    `https://nominatim.openstreetmap.org/reverse?format=json&lat=${newLat}&lon=${newLng}`
                  );
                  const data = await response.json();
                  const address = data.display_name || `${newLat}, ${newLng}`;

                  setFieldValue('location', address);
                  setInputValue(address);
                } catch (error) {
                  console.error('Reverse geocoding error:', error);
                }
              }
            }}
          />

          <MapUpdater position={position} />
        </MapContainer>
      )}
    </div>
  );
};

export default LocationSearch;
